import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setAuthToken, getAuthToken, formatApiErrorDetail } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [company, setCompany] = useState(null);

  const refresh = useCallback(async () => {
    // Altijd /auth/me proberen: als de gebruiker een geldige httpOnly cookie heeft (server-side
    // auth pad blijft werken zelfs als de client geen in-memory/localStorage token heeft — bv.
    // iOS Safari Private Mode bij een harde page-reload of deeplink).
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company);
    } catch (e) {
      setUser(false);
      setCompany(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password, remember = true) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      if (data.access_token) setAuthToken(data.access_token, remember);
      if (data.user) setUser(data.user);
      try {
        const { data: me } = await api.get("/auth/me");
        if (me.user) setUser(me.user);
        setCompany(me.company);
      } catch (_) { /* silently ignore — user object from login is enough to navigate */ }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* noop */ }
    setAuthToken(null);
    setUser(false);
    setCompany(null);
  }, []);

  // Memoize the context value so children only re-render when user/company actually change.
  const value = useMemo(
    () => ({ user, company, login, logout, refresh, setCompany }),
    [user, company, login, logout, refresh],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
